import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useScrollToTop from "./../../hooks/useScollToTop";
import { HiOutlineMail, HiOutlineUser, HiOutlineLockClosed, HiOutlineEye, HiOutlineEyeOff } from "react-icons/hi";
import { registerUser, checkEmailExists } from "../../services/userServices";
import { sendNotificationToAdmin } from "../../services/notificationServices";
import { getSettings } from "../../services/settingsServices";
import Modal from "../../hooks/Modal";
import Button from "../../components/Button/Button";

/* ========= Helpers identiques à ErrorPage/LoginPage ========= */
function toAbsolute(u = "") {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/")) return `${window.location.origin}${u}`;
  return u;
}
function pickIndex(n, exclude = -1) {
  if (n <= 1) return 0;
  let idx = Math.floor(Math.random() * n);
  if (idx === exclude) idx = (idx + 1) % n;
  return idx;
}
/* =========================================================== */

export default function RegisterPage() {
  useScrollToTop();

  const navigate = useNavigate();

  const [bgUrl, setBgUrl] = useState(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // ✅ Fond dynamique identique à ErrorPage/LoginPage (avec anti-répétition)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getSettings();
        const s = Array.isArray(data) ? data[0] : data;

        const raw = [s?.photoURL1, s?.photoURL2, s?.photoURL3].filter(
          (u) => typeof u === "string" && u.trim().length > 0
        );
        const unique = Array.from(new Set(raw));
        const candidates = unique.map(toAbsolute);

        if (!mounted) return;

        if (candidates.length > 0) {
          const prev = Number(sessionStorage.getItem("registerBgIdx"));
          const idx = pickIndex(
            candidates.length,
            Number.isNaN(prev) ? -1 : prev
          );
          sessionStorage.setItem("registerBgIdx", String(idx));
          setBgUrl(candidates[idx]);
        } else {
          setBgUrl(null);
        }
      } catch (e) {
        console.error("RegisterPage getSettings:", e);
        if (mounted) setBgUrl(null);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const handleResetPassword = () => {
    closeModal();
    navigate("/forgot-password");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setLoading(true);
    try {
      // Vérif email déjà existant
      const emailCheck = await checkEmailExists(email);
      if (emailCheck?.error) {
        setErrorMessage(emailCheck.error);
        setLoading(false);
        return;
      }
      if (emailCheck?.exists) {
        setLoading(false);
        openModal();
        return;
      }

      const result = await registerUser(nom, prenom, email, password);
      if (result?.error) {
        setErrorMessage(result.error);
        setLoading(false);
        return;
      }

      // Notification admin (best-effort, non bloquant)
      try {
        const token = localStorage.getItem("token");
        const message = `Un nouvel utilisateur a été créé : ${nom} ${prenom} (${email})`;
        await sendNotificationToAdmin(message, token);
      } catch (err) {
        console.warn("Notification admin échouée (non bloquant):", err);
      }

      navigate("/login");
    } catch (err) {
      console.error("register error:", err);
      setErrorMessage(
        "Inscription impossible pour le moment. Réessayez plus tard."
      );
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen">
      {/* Fond image ou dégradé */}
      <div
        className={`absolute inset-0 ${
          bgUrl
            ? "bg-cover bg-center bg-no-repeat"
            : "bg-gradient-to-b from-slate-900 to-slate-700"
        }`}
        style={bgUrl ? { backgroundImage: `url(${bgUrl})` } : undefined}
        aria-hidden="true"
      />
      {/* Voile */}
      <div
        className="absolute inset-0 bg-slate-900/50 dark:bg-slate-950/60"
        aria-hidden="true"
      />

      {/* Contenu */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Header / badge */}
          <div className="mb-4 text-center text-slate-100">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 text-xs uppercase tracking-wide border border-white/20 mb-3">
              Création de compte
            </span>
            <h1 className="text-3xl font-bold drop-shadow-sm">
              Inscription à MonAppliMairie
            </h1>
            <p className="mt-1 text-sm text-slate-200/90">
              Renseignez vos informations pour accéder à votre espace.
            </p>
          </div>

          {/* Carte d’inscription */}
          <div className="bg-white/80 dark:bg-slate-900/90 backdrop-blur-md rounded-2xl shadow-xl border border-white/40 dark:border-slate-800/70 p-6 md:p-7">
            {/* Erreur globale */}
            {errorMessage && (
              <div
                className="mb-4 rounded-xl border border-red-200 bg-red-50/90 text-red-800 text-sm px-3 py-2 flex items-start gap-2 dark:bg-red-900/40 dark:border-red-800 dark:text-red-100"
                role="alert"
              >
                <span className="mt-0.5">⚠️</span>
                <span>{errorMessage}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Nom */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                  Nom
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-3 flex items-center text-slate-400">
                    <HiOutlineUser className="h-5 w-5" />
                  </span>
                  <input
                    type="text"
                    placeholder="Votre nom"
                    className="mt-0 block w-full pl-10 pr-3 py-2.5 bg-white/90 border border-slate-300 rounded-xl text-sm shadow-sm placeholder-slate-400
                               focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/60
                               dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500"
                    value={nom}
                    onChange={(e) => setNom(e.target.value)}
                    autoComplete="family-name"
                    required
                  />
                </div>
              </div>

              {/* Prénom */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                  Prénom
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-3 flex items-center text-slate-400">
                    <HiOutlineUser className="h-5 w-5" />
                  </span>
                  <input
                    type="text"
                    placeholder="Votre prénom"
                    className="mt-0 block w-full pl-10 pr-3 py-2.5 bg-white/90 border border-slate-300 rounded-xl text-sm shadow-sm placeholder-slate-400
                               focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/60
                               dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500"
                    value={prenom}
                    onChange={(e) => setPrenom(e.target.value)}
                    autoComplete="given-name"
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                  Email
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-3 flex items-center text-slate-400">
                    <HiOutlineMail className="h-5 w-5" />
                  </span>
                  <input
                    type="email"
                    placeholder="votre.email@mairie.fr"
                    className="mt-0 block w-full pl-10 pr-3 py-2.5 bg-white/90 border border-slate-300 rounded-xl text-sm shadow-sm placeholder-slate-400
                               focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/60
                               dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              {/* Mot de passe + toggle */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                  Mot de passe
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-3 flex items-center text-slate-400 pointer-events-none">
                    <HiOutlineLockClosed className="h-5 w-5" />
                  </span>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Choisissez un mot de passe"
                    className="mt-0 block w-full pl-10 pr-10 py-2.5 bg-white/90 border border-slate-300 rounded-xl text-sm shadow-sm placeholder-slate-400
                               focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/60
                               dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-2 flex items-center px-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                    aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  >
                    {showPassword ? (
                      <HiOutlineEyeOff className="h-5 w-5" />
                    ) : (
                      <HiOutlineEye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Utilisez au moins 8 caractères, avec lettres et chiffres.
                </p>
              </div>

              {/* Bouton */}
              <button
                className={`w-full px-4 py-2.5 rounded-xl shadow-sm border text-sm font-medium flex items-center justify-center gap-2
                  transition-transform duration-150
                  ${
                    loading
                      ? "bg-slate-400 text-white cursor-not-allowed border-slate-300"
                      : "bg-primary text-white border-primary hover:bg-white hover:text-primary transform hover:-translate-y-0.5 hover:shadow-md"
                  }`}
                type="submit"
                disabled={loading}
              >
                {loading && (
                  <span className="inline-flex h-4 w-4 rounded-full border-2 border-white/60 border-t-transparent animate-spin" />
                )}
                <span>{loading ? "Création du compte..." : "S'inscrire"}</span>
              </button>

              <div className="pt-2 border-t border-slate-100 dark:border-slate-800 text-center text-xs text-slate-500 dark:text-slate-400">
                Si vous possédez déjà un compte, utilisez la page de connexion
                ou la fonction « mot de passe oublié ».
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Modale email déjà utilisé */}
      {isModalOpen && (
        <Modal isOpen={isModalOpen} onClose={closeModal}>
          <div className="p-2">
            <p className="mb-4 text-slate-800 dark:text-slate-100">
              Cet email est déjà enregistré. Souhaitez-vous réinitialiser votre mot de passe ?
            </p>
            <div className="flex justify-center gap-4">
              <Button text="Oui" onClick={handleResetPassword} />
              <Button text="Non" onClick={closeModal} />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
